import { Anchor } from '@components/atoms/Anchor';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import { Heading1 } from '@components/atoms/Typography';
import PageContainer from '@components/molecules/PageContainer';
import BREAKPOINTS from '@lib/constants/breakpoints';
import env from '@lib/constants/env';
import ROUTES from '@lib/constants/routes';
import motorcycleStats from '@lib/helpers/motorcycleStats';
import { GetServerSideProps } from 'next';
import { BreadcrumbJsonLd } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

interface Props {
  stats: any[];
  brand: string;
}

const List = styled.ul`
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;
  }
`;

const Header = styled(Section)`
  padding-top: 120px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 50px;
  }
`;

const BrandStats = ({ stats, brand }: Props) => {
  const router = useRouter();

  if (router.isFallback) return <PageContainer loading />;

  return (
    <PageContainer
      title={`Ficha técnica - Modelo da ${brand}`}
      metaDescription={`Ficha técnica com as especificações completas da ${brand} . Veja detalhes de motor, performance, valores, seguro e muito mais nesse guia!`}
    >
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Luna',
            item: env.baseUrl,
          },
          {
            position: 2,
            name: 'Ficha técnica',
            item: `${env.baseUrl}${ROUTES.stats}`,
          },
          {
            position: 3,
            name: `Ficha técnica para motos da ${brand}`,
            item: `${env.baseUrl}${ROUTES.brandStats(stats[0].brand)}`,
          },
        ]}
      />
      <Header>
        <Heading1>
          Modelos da
          {` ${brand}`}
        </Heading1>
        <Spacing top={2}>
          Todas as fichas técnicas de modelos de motos da
          {' '}
          {brand}
        </Spacing>
      </Header>
      <Section>
        <List>
          {stats.map((s) => (
            <li key={s.id}>
              <Link href={ROUTES.modelStats(s.brand, s.model)}>
                <Anchor>{s.modelName}</Anchor>
              </Link>
            </li>
          ))}
        </List>
      </Section>
    </PageContainer>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({ params }) => {
  const brand = params?.brand?.toString() || '';

  const stats = await motorcycleStats.getFromBrand(brand);

  if (!stats.length) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      stats,
      brand: stats[0].brandName,
    },
  };
};

export default BrandStats;
